// The office's week (round 44): everybody expected to hand a timesheet in,
// where each one is, and who is missing.
//
// The week and the filter live in the URL (?week=, ?status=), so "last week's
// missing" is a link Home and the reminders can send people to. Sorting is
// component state, never the URL (round 34's rule). Every chip renders,
// including at zero, so the strip keeps its shape.
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../../api';
import { useSession } from '../../session';
import { Banner } from '../../ui';
import { StatusBadge } from '../ui/StatusBadge';
import { CardList, RowCard } from '../ui/RowCard';
import { LinkedRow } from '../ui/LinkedRow';
import { SortHeader } from '../ui/SortHeader';
import { sortRows, useSort } from '../../lib/sortRows';
import { useLatestRequest } from '../../lib/useLatestRequest';
import { addDays } from '../../lib/dates';
import { minutesToHm } from '../../lib/timesheetHours';
import { formatMomentAu } from '../../lib/dates';
import {
  TIMESHEET_STATUS_LABEL,
  timesheets,
  type Board,
  type BoardRow,
  type BoardStatus,
} from '../../timesheetsApi';

const CHIPS: (BoardStatus | 'all')[] = ['all', 'missing', 'draft', 'submitted', 'returned', 'approved'];
type SortKey = 'name' | 'status' | 'worked' | 'leave' | 'signed' | 'updated';
const STATUS_ORDER: Record<BoardStatus, number> = { missing: 0, returned: 1, draft: 2, submitted: 3, approved: 4 };

export function TimesheetBoard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const week = params.get('week') ?? undefined;
  const filter = (params.get('status') as BoardStatus | null) ?? 'all';
  const [board, setBoard] = useState<Board | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const latest = useLatestRequest();
  const { sortKey, sortDir, onSort } = useSort<SortKey>('status');
  const { user } = useSession();

  useEffect(() => {
    const isCurrent = latest();
    timesheets
      .board(week)
      .then((next) => isCurrent() && setBoard(next))
      .catch((err: unknown) => isCurrent() && setError(err instanceof ApiError ? err.message : 'Could not load the timesheets.'));
  }, [week, latest, reload]);

  /**
   * Approve from the row, for the Monday-morning run through a crew's weeks
   * when the hours on the board are what the manager checks. Same rule as
   * the week's own page: nobody approves their own, except an admin; the
   * server judges it again under the row lock.
   */
  const canApproveRow = (row: BoardRow) =>
    row.status === 'submitted' &&
    row.weekId !== null &&
    row.lockVersion !== null &&
    (row.userId !== user?.id || user?.role === 'admin');

  async function approve(row: BoardRow) {
    if (!row.weekId || row.lockVersion === null) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await timesheets.approve(row.weekId, row.lockVersion);
      setNotice(`Approved ${row.name}’s week. They have been emailed.`);
      setReload((n) => n + 1);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 409
          ? `${row.name}’s week changed since this list loaded. Open it to check before approving.`
          : err instanceof ApiError
            ? err.message
            : 'Could not approve that. Try again.'
      );
      setReload((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }

  function setParam(key: 'week' | 'status', value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  const rows = useMemo(() => {
    const all = board?.rows ?? [];
    const filtered = filter === 'all' ? all : all.filter((r) => r.status === filter);
    return sortRows(
      filtered,
      (r: BoardRow) =>
        sortKey === 'name'
          ? r.name
          : sortKey === 'status'
            ? STATUS_ORDER[r.status]
            : sortKey === 'worked'
              ? (r.totals?.workedMinutes ?? null)
              : sortKey === 'leave'
                ? (r.totals ? r.totals.leaveMinutes + r.totals.fullLeaveDays * 1440 : null)
                : sortKey === 'signed'
                  ? (r.signed ? 'Signed' : r.officeEntered ? 'Office' : null)
                  : r.updatedAt,
      sortDir
    );
  }, [board, filter, sortKey, sortDir]);

  async function startFor(row: BoardRow) {
    if (!board) return;
    setBusy(true);
    try {
      const detail = await timesheets.createWeek(board.weekStart, row.userId);
      navigate(`/app/timesheets/${detail.sheet.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start that week.');
      setBusy(false);
    }
  }

  if (!board) return error ? <Banner kind="error">{error}</Banner> : <p className="muted">Loading…</p>;

  const isThisWeek = board.weekStart === board.thisWeekStart;
  const isLastWeek = board.weekStart === board.lastWeekStart;
  const open = (row: BoardRow) => (row.weekId ? `/app/timesheets/${row.weekId}` : undefined);
  const action = (row: BoardRow) =>
    row.weekId ? (
      <span className="inline-flex flex-wrap justify-end gap-2">
        {canApproveRow(row) && (
          <button type="button" className="btn-primary btn-sm" disabled={busy} onClick={() => void approve(row)}>
            Approve
          </button>
        )}
        <Link className="btn-secondary btn-sm" to={`/app/timesheets/${row.weekId}`}>
          {row.status === 'submitted' ? 'Review' : 'Open'}
        </Link>
      </span>
    ) : (
      <button type="button" className="btn-secondary btn-sm" disabled={busy} onClick={() => void startFor(row)}>
        Enter it for them
      </button>
    );

  return (
    <>
      {error && <Banner kind="error">{error}</Banner>}
      {notice && <Banner kind="ok">{notice}</Banner>}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-secondary btn-sm"
            aria-label="The week before"
            onClick={() => setParam('week', addDays(board.weekStart, -7))}
          >
            ‹ Earlier
          </button>
          <div className="min-w-0 px-1 text-center">
            <p className="!mb-0 text-sm font-semibold text-gray-900">{board.weekLabel}</p>
            <p className="!mb-0 text-xs text-gray-500">
              {isThisWeek ? 'This week' : isLastWeek ? 'Last week' : 'Week'}
            </p>
          </div>
          <button
            type="button"
            className="btn-secondary btn-sm"
            aria-label="The week after"
            disabled={isThisWeek}
            onClick={() => setParam('week', addDays(board.weekStart, 7))}
          >
            Later ›
          </button>
        </div>
        <div className="flex gap-4">
          {!isLastWeek && (
            <button type="button" className="row-action min-h-11" onClick={() => setParam('week', null)}>
              Last week
            </button>
          )}
          {!isThisWeek && (
            <button
              type="button"
              className="row-action min-h-11"
              onClick={() => setParam('week', board.thisWeekStart)}
            >
              This week
            </button>
          )}
        </div>
      </div>

      {board.awaitingElsewhere.length > 0 && (
        <p className="banner-info mb-4" role="status">
          Also waiting for approval:{' '}
          {board.awaitingElsewhere.map((w, i) => (
            <span key={w.weekStart}>
              {i > 0 && ', '}
              <button
                type="button"
                className="row-action"
                onClick={() => {
                  const next = new URLSearchParams(params);
                  next.set('week', w.weekStart);
                  next.set('status', 'submitted');
                  setParams(next, { replace: true });
                }}
              >
                {w.count} for {w.weekLabel}
              </button>
            </span>
          ))}
        </p>
      )}

      {board.counts.all > 0 && (
        <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Show">
          {CHIPS.map((chip) => {
            const on = filter === chip;
            return (
              <button
                key={chip}
                type="button"
                aria-pressed={on}
                onClick={() => setParam('status', chip === 'all' ? null : chip)}
                className={`min-h-[44px] rounded-full px-3 text-sm sm:min-h-[32px] ${
                  on ? 'bg-brand-500 font-medium text-ink' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {chip === 'all' ? 'Everyone' : TIMESHEET_STATUS_LABEL[chip]} ({board.counts[chip]})
              </button>
            );
          })}
        </div>
      )}

      {board.counts.all === 0 ? (
        <section className="panel">
          <p className="!mb-0">
            Nobody is set up to fill in timesheets yet. Field workers are by default once they have accepted their
            invitation; an admin can add others under Settings, Timesheets.
          </p>
        </section>
      ) : rows.length === 0 ? (
        <section className="panel">
          <p className="!mb-0 muted">Nobody here this week.</p>
        </section>
      ) : (
        <>
          <CardList>
            {rows.map((row) => (
              <RowCard
                key={row.userId}
                to={open(row)}
                title={row.name}
                badges={<StatusBadge kind="timesheet" status={row.status} />}
                actions={row.weekId && !canApproveRow(row) ? undefined : action(row)}
              >
                {row.position && <div>{row.position}</div>}
                {row.totals && (
                  <div className="tabular-nums">
                    {minutesToHm(row.totals.workedMinutes)} worked
                    {row.totals.fullLeaveDays > 0 && <> · {row.totals.fullLeaveDays} day{row.totals.fullLeaveDays === 1 ? '' : 's'} leave</>}
                  </div>
                )}
                {row.officeEntered && <div>Entered by the office</div>}
              </RowCard>
            ))}
          </CardList>

          <div className="table-wrap max-sm:hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader label="Person" sortKey="name" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Status" sortKey="status" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Worked" sortKey="worked" current={sortKey} dir={sortDir} onSort={onSort} className="text-right" />
                  <SortHeader label="Leave" sortKey="leave" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Signed" sortKey="signed" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Last change" sortKey="updated" current={sortKey} dir={sortDir} onSort={onSort} />
                  <th className="table-header">
                    <span className="sr-only">Action</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const cells = (
                    <>
                      <td>
                        {row.weekId ? (
                          <Link className="table-link" to={`/app/timesheets/${row.weekId}`}>
                            {row.name}
                          </Link>
                        ) : (
                          row.name
                        )}
                        {row.position && <div className="table-muted">{row.position}</div>}
                      </td>
                      <td>
                        <StatusBadge kind="timesheet" status={row.status} />
                      </td>
                      <td className="text-right tabular-nums">{row.totals ? minutesToHm(row.totals.workedMinutes) : ''}</td>
                      <td className="table-muted">
                        {row.totals
                          ? [
                              row.totals.leaveMinutes > 0 ? minutesToHm(row.totals.leaveMinutes) : null,
                              row.totals.fullLeaveDays > 0 ? `${row.totals.fullLeaveDays} full day${row.totals.fullLeaveDays === 1 ? '' : 's'}` : null,
                            ]
                              .filter(Boolean)
                              .join(' + ')
                          : ''}
                      </td>
                      <td className="table-muted">
                        {row.status !== 'submitted' && row.status !== 'approved' ? '' : row.signed ? 'Signed' : row.officeEntered ? 'Office' : ''}
                      </td>
                      <td className="table-muted">{row.updatedAt ? formatMomentAu(row.updatedAt) : ''}</td>
                      <td className="text-right">{action(row)}</td>
                    </>
                  );
                  return row.weekId ? (
                    <LinkedRow key={row.userId} to={`/app/timesheets/${row.weekId}`}>
                      {cells}
                    </LinkedRow>
                  ) : (
                    <tr key={row.userId}>{cells}</tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
